import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Button, Grid, GridItem, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react'
import { getCostProfitMatricesByProjectId, getNegativeChangeOrdersAmountByProjectId } from 'api'
import { numberFormatter } from 'lib'
import { useAppContext } from 'hooks/useAppContext'

export const ModalPriceBreakdown = ({ isOpen, onClose, projectId, customerCompany }) => {
  const { createToast } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [cpm, setCpm] = useState(null)
  const [negativeChangeOrdersAmount, setNegativeChangeOrdersAmount] = useState(0)

  useEffect(() => {
    const loadCpm = async () => {
      setIsLoading(true)
      try {
        const response = await getCostProfitMatricesByProjectId(projectId)
        const amount = await getNegativeChangeOrdersAmountByProjectId(projectId)
        setNegativeChangeOrdersAmount(amount)
        setCpm(response)
        setIsLoading(false)
      } catch (err) {
        createToast('error', 'Error', 'Failed to load cpm data.')
      }
    }

    if (isOpen) {
      loadCpm()
    }
  }, [projectId])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Price Breakdown. Project #{projectId} - {customerCompany}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Text fontSize="sm">Loading ...</Text>
          ) : (
            <Grid
              templateColumns="1fr 1fr"
              gap={4}
            >
              <GridItem>
                <PriceRow label="EMC" price={cpm?.emcText || 0} />
                <PriceRow label="Monument" price={cpm?.monumentText || 0} />
                <PriceRow label="Shipping" price={cpm?.shippingText || 0} />
                <PriceRow label="Tax" price={cpm?.taxText || 0} />
                <PriceRow label="Discount" price={cpm?.discount || 0} />
                <PriceRow label="Negative Change Orders Amount" price={negativeChangeOrdersAmount || 0} />
              </GridItem>
              <GridItem>
                <PriceRow label="Fabrication" price={cpm?.fabricationText || 0} />
                <PriceRow label="Steel" price={cpm?.steelText || 0} />
                <PriceRow label="Install" price={cpm?.installationText || 0} />
                <PriceRow label="Overage" price={cpm?.overage || 0} />
                <PriceRow label="Total" price={cpm?.grossSale || 0} />
                <PriceRow label="Commissionable Amount" price={(cpm?.grossSale || 0) - (cpm?.shippingText || 0) - (cpm?.installationText || 0) - (cpm?.taxText || 0) - (cpm?.overage || 0) + (negativeChangeOrdersAmount || 0)} />
              </GridItem>
            </Grid>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalPriceBreakdown.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  projectId: PropTypes.number,
  customerCompany: PropTypes.string
}

const PriceRow = ({ label, price, size = 'sm', ...rest }) => {
  return (
    <Grid templateColumns="1fr 1fr" columnGap={4} {...rest}>
      <GridItem>
        <Text fontSize={size}>{label} : </Text>
      </GridItem>
      <GridItem>
        <Text fontSize={size}>$ {numberFormatter(price)}</Text>
      </GridItem>
    </Grid>
  )
}

PriceRow.propTypes = {
  label: PropTypes.string,
  price: PropTypes.number,
  size: PropTypes.string
}
